(() => {
  const root = document;

  function initReveal() {
    const els = root.querySelectorAll('.impasto-landing .reveal');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
  }

  function initWordReveal() {
    const els = root.querySelectorAll('[data-word-reveal]');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.25 }
    );
    els.forEach((el) => io.observe(el));
  }

  function initMarquee(el) {
    const track = el.querySelector('.impasto-marquee__track');
    if (!track) return;

    const direction = el.dataset.direction === 'right' ? 'right' : 'left';
    const speed = direction === 'left' ? -0.45 : 0.45;
    let offset = 0;
    let halfWidth = 0;
    let dragging = false;
    let paused = false;
    let lastX = 0;
    let primed = false;
    let raf = 0;

    const wrap = () => {
      if (halfWidth <= 0) return;
      // Strict bounds so left can start at 0 and right at -halfWidth
      while (offset < -halfWidth) offset += halfWidth;
      while (offset > 0) offset -= halfWidth;
    };

    const paint = () => {
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    };

    const measure = () => {
      // Measure on load/resize only — reading scrollWidth every frame causes flicker
      const next = track.scrollWidth / 2;
      if (next <= 0) return;
      halfWidth = next;
      if (!primed) {
        offset = direction === 'right' ? -halfWidth : 0;
        primed = true;
      }
      wrap();
      paint();
    };

    const tick = () => {
      if (!dragging && !paused && halfWidth > 0) {
        offset += speed;
        wrap();
        paint();
      }
      raf = requestAnimationFrame(tick);
    };

    measure();
    raf = requestAnimationFrame(tick);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(track);
    track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', measure, { once: true });
    });
    window.addEventListener('resize', measure, { passive: true });

    el.addEventListener('mouseenter', () => {
      paused = true;
    });
    el.addEventListener('mouseleave', () => {
      paused = false;
    });

    el.addEventListener('pointerdown', (e) => {
      dragging = true;
      lastX = e.clientX;
      el.setPointerCapture?.(e.pointerId);
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      offset += dx;
      wrap();
      paint();
    });
    const endDrag = () => {
      dragging = false;
    };
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    el._impastoMarquee = { raf, ro };
  }

  function initBeforeAfter(el) {
    const after = el.querySelector('[data-ba-after]');
    const handle = el.querySelector('[data-ba-handle]');
    if (!after || !handle) return;
    let pos = 50;
    let dragging = false;

    const paint = () => {
      after.style.clipPath = `inset(0 0 0 ${pos}%)`;
      handle.style.left = `${pos}%`;
    };
    paint();

    const update = (clientX) => {
      const rect = el.getBoundingClientRect();
      pos = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      paint();
    };

    el.addEventListener('pointerdown', (e) => {
      dragging = true;
      el.setPointerCapture?.(e.pointerId);
      update(e.clientX);
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      update(e.clientX);
    });
    const end = () => {
      dragging = false;
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
  }

  function initSticky(el) {
    const onScroll = () => {
      const hero = document.querySelector('.impasto-hero');
      const threshold = hero ? hero.offsetHeight * 0.7 : 480;
      el.classList.toggle('is-visible', window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initWordScroll() {
    const els = [...root.querySelectorAll('[data-word-scroll]')];
    if (!els.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      els.forEach((el) => {
        el.querySelectorAll('.impasto-word').forEach((word) => word.classList.add('is-lit'));
      });
      return;
    }

    const blocks = els.map((el) => ({
      el,
      words: [...el.querySelectorAll('.impasto-word')],
    }));

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || 1;

      blocks.forEach(({ el, words }) => {
        if (!words.length) return;
        const rect = el.getBoundingClientRect();
        // Light words as the block moves through the middle of the viewport
        const start = vh * 0.9;
        const end = vh * 0.28;
        const raw = (start - rect.top) / (start - end);
        const progress = Math.min(1, Math.max(0, raw));
        const exact = progress * words.length;
        const litCount = Math.floor(exact);
        const partial = exact - litCount;

        words.forEach((word, i) => {
          word.classList.toggle('is-lit', i < litCount);
          word.classList.toggle('is-lit-partial', i === litCount && partial > 0.15);
        });
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const scrollRoot = document.querySelector('.page-wrapper') || window;
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function boot() {
    initReveal();
    initWordReveal();
    initWordScroll();
    root.querySelectorAll('.impasto-marquee').forEach(initMarquee);
    root.querySelectorAll('[data-impasto-ba]').forEach(initBeforeAfter);
    root.querySelectorAll('[data-impasto-sticky]').forEach(initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
